import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import { fetchMeetingRouting, saveMeetingRouting, type MeetingRouting } from '@/lib/queries';
import { useIsAdmin } from '@/hooks/use-scopes';
import { cn } from '@/lib/cn';
import { useCollapsed, CollapseChevron } from '@/components/ui/CollapsibleCard';

const errMsg = (e: unknown) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not save';
const LINES = ['1st', '2nd', '3rd', '4th'];

/** Who takes the clinic's meetings, by topic (Aziz 2026-10-01). The clinic never picks a
 *  person: it says what the meeting is about, and the first person on that topic's chain
 *  with free time in their booking horizon owns the call. Each line is a seat (whoever
 *  holds it on this clinic today) or a named person. */
export default function MeetingRoutingCard({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const isAdmin = useIsAdmin();
  const { open: sectionOpen, toggle: toggleSection } = useCollapsed('client.meeting-routing.open');
  const q = useQuery({ queryKey: ['meeting-routing', clientId], queryFn: () => fetchMeetingRouting(clientId) });
  const [draft, setDraft] = useState<Record<string, string[]> | null>(null);
  const data: MeetingRouting | undefined = q.data;
  const chainOf = (key: string) => draft?.[key] ?? data?.topics.find((t) => t.key === key)?.chain ?? [];
  const setLine = (key: string, i: number, value: string) => {
    const base: Record<string, string[]> = {};
    for (const t of data?.topics ?? []) base[t.key] = [...chainOf(t.key)];
    const chain = base[key];
    if (value) chain[i] = value; else chain.splice(i, 1);
    base[key] = chain.filter((x, idx) => x && chain.indexOf(x) === idx).slice(0, 4);
    setDraft(base);
  };
  const save = useMutation({
    mutationFn: () => saveMeetingRouting(clientId, draft ?? {}),
    onSuccess: () => { toast.success('Meeting routing saved'); setDraft(null); qc.invalidateQueries({ queryKey: ['meeting-routing', clientId] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (q.isLoading) return <Card className="mb-5"><div className="text-xs text-muted">Loading meeting routing...</div></Card>;
  if (!data) return null;
  const options = data.options.map((o) => ({ value: o.value, label: o.label, hint: o.kind === 'seat' ? 'whoever holds the seat here' : undefined }));

  return (
    <Card className="mb-5" data-testid="meeting-routing">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <button type="button" onClick={toggleSection} aria-expanded={sectionOpen}
          className="font-medium text-muted-strong text-sm inline-flex items-center gap-2 text-left">
          <CollapseChevron open={sectionOpen} /><CalendarClock size={14} /> Meetings: who takes the clinic's calls
        </button>
        {draft && isAdmin && (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Discard</Button>
            <Button size="sm" icon={<Check size={12} />} onClick={() => save.mutate()} loading={save.isPending} data-testid="meeting-routing-save">Save</Button>
          </div>
        )}
      </div>
      {sectionOpen && (<>
        <p className="text-[11px] text-muted mb-3">
          The clinic asks for a meeting by topic, never by person. The first line owns that topic's calls; when they have no free time in their booking horizon, the next line takes it. A seat means whoever holds it on this clinic today.
        </p>
        <div className="space-y-3">
          {data.topics.map((t) => (
            <div key={t.key} className="rounded-lg border border-slate-800 px-3 py-2.5" data-testid={`meeting-topic-${t.key}`}>
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mb-2">
                <span className="text-sm text-muted-strong">{t.label}</span>
                <span className="text-[11px] text-muted">{t.hint}</span>
                {t.is_default && !draft?.[t.key] && <span className="text-[10px] text-muted font-mono">DEFAULT</span>}
              </div>
              <div className="flex flex-wrap gap-2 items-end">
                {[0, 1, 2, 3].filter((i) => i <= chainOf(t.key).length && i < 4).map((i) => (
                  <div key={i} className="w-48">
                    <Select size="sm" label={`${LINES[i]} line`} value={chainOf(t.key)[i] ?? ''} onChange={(v) => setLine(t.key, i, v)}
                      placeholder={i === 0 ? 'Pick who' : 'Add a line'} searchable
                      options={[...(i < chainOf(t.key).length ? [{ value: '', label: 'Remove this line' }] : []), ...options]} />
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-muted mt-2" data-testid={`meeting-takes-${t.key}`}>
                {t.lines.map((ln, i) => (
                  <span key={i} className={cn('mr-3', i > 0 && 'opacity-80')}>
                    {LINES[i]}: {ln.label}{ln.names.length ? ` (${ln.names.map((n, j) => `${n}${ln.free[j] ? '' : ', no time'}`).join(', ')})` : ' (nobody yet)'}
                  </span>
                ))}
                {t.takes_it ? <span className="text-muted-strong">Today: {t.takes_it}</span> : <span className="text-amber-300">Today: nobody; write in Chat</span>}
              </div>
            </div>
          ))}
        </div>
      </>)}
    </Card>
  );
}
